import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

const CrossedBadge = ({ label }) => (
  <div className="relative rounded-xl border-2 border-dashed border-pav/40 px-4 py-2">
    <span className="font-bungee text-xs sm:text-sm tracking-widest text-pav/50">{label}</span>
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="absolute h-0.5 w-[105%] rotate-6 bg-mirchi" />
      <div className="absolute h-0.5 w-[105%] -rotate-6 bg-mirchi" />
    </div>
  </div>
);

const Chilli = ({ className = "" }) => (
  <svg viewBox="0 0 120 44" className={className}>
    <ellipse cx="52" cy="24" rx="44" ry="14" fill="#2E9E52" stroke="#221A10" strokeWidth="5" />
    <path d="M92 16 q18 -10 22 6" stroke="#221A10" strokeWidth="6" fill="none" strokeLinecap="round" />
  </svg>
);

const Steam = ({ className = "" }) => (
  <svg viewBox="0 0 200 60" className={className} fill="none" stroke="#FFF5E1" strokeWidth="6" strokeLinecap="round">
    <path d="M50 48 q10 -14 0 -28" />
    <path d="M100 52 q10 -14 0 -28" />
    <path d="M150 48 q10 -14 0 -28" />
  </svg>
);

const Scene = ({ p }) => {
  const vadaX = useTransform(p, [0.05, 0.7], [0.22, 1]);
  const pavX = useTransform(p, [0.2, 0.8], [0.3, 1]);
  const chutneyX = useTransform(p, [0.3, 0.85], [0.36, 1]);
  const chilliY = useTransform(p, [0.8, 0.96], [-180, 0]);
  const chilliO = useTransform(p, [0.78, 0.88], [0, 1]);
  const steamO = useTransform(p, [0.85, 1], [0, 0.7]);
  const oldO = useTransform(p, [0.3, 0.5], [1, 0]);
  const newO = useTransform(p, [0.55, 0.75], [0, 1]);
  const uncurlO = useTransform(p, [0.15, 0.35], [1, 0]);
  const rulerX = useTransform(p, [0.15, 0.75], [0.18, 1]);
  const stamp = useTransform(p, [0.9, 1], [0, 1]);

  return (
    <>
      <div className="relative mt-12 h-[250px] w-[min(88vw,860px)] sm:h-[280px]">
        <motion.div style={{ opacity: steamO }} className="absolute left-0 right-0 -top-14 mx-auto w-48">
          <Steam className="w-full" />
        </motion.div>
        <motion.div
          style={{ scaleX: pavX }}
          className="absolute left-0 right-0 top-10 mx-auto h-14 w-full origin-center rounded-full border-4 border-black bg-[#F2B25C] sm:h-16"
        />
        <motion.div
          style={{ scaleX: vadaX }}
          className="vada-texture absolute left-0 right-0 top-[92px] mx-auto h-20 w-full origin-center rounded-full border-4 border-black sm:top-[96px] sm:h-24"
        />
        <motion.div
          style={{ scaleX: chutneyX }}
          className="absolute left-0 right-0 top-[176px] mx-auto h-3 w-full origin-center rounded-full bg-chutney sm:top-[188px]"
        />
        <motion.div
          style={{ scaleX: pavX }}
          className="absolute left-0 right-0 top-[190px] mx-auto h-14 w-full origin-center rounded-full border-4 border-black bg-[#F2B25C] sm:top-[202px] sm:h-16"
        />
        <motion.div style={{ y: chilliY, opacity: chilliO }} className="absolute left-[52%] top-2 w-28 -rotate-12 sm:w-36">
          <Chilli className="w-full" />
        </motion.div>
        <motion.span
          style={{ opacity: uncurlO }}
          className="absolute -left-2 -top-6 -rotate-6 font-shrikhand text-xl text-mirchi sm:text-2xl"
        >
          uncurl! →
        </motion.span>
      </div>

      <div className="relative mt-4 flex h-10 w-full justify-center">
        <motion.span style={{ opacity: oldO }} className="absolute font-bungee text-sm tracking-[0.3em] text-pav sm:text-base">
          1966 • THE ROUND OG
        </motion.span>
        <motion.span style={{ opacity: newO }} className="absolute font-bungee text-sm tracking-[0.3em] text-taxi sm:text-base">
          2026 • THE LONG VADA™
        </motion.span>
      </div>

      <div className="mt-6 w-[min(88vw,860px)]">
        <motion.div style={{ scaleX: rulerX }} className="h-2.5 origin-left rounded-full bg-taxi" />
        <div className="mt-2 flex justify-between">
          {Array.from({ length: 17 }).map((_, i) => (
            <div key={i} className={`w-0.5 bg-pav/60 ${i % 4 === 0 ? "h-5" : "h-3"}`} />
          ))}
        </div>
        <div className="mt-1 flex justify-between font-anton text-sm uppercase text-pav">
          <span>Chota</span>
          <span className="text-taxi">Lamba karo — naap ke dekho</span>
          <span>Bada</span>
        </div>
      </div>

      <motion.div
        style={{ scale: stamp }}
        className="absolute right-4 top-28 rotate-12 rounded-xl border-4 border-gulabi px-4 py-2 font-bungee text-sm text-gulabi sm:right-16"
      >
        ELONGIFIED™
      </motion.div>
    </>
  );
};

const StaticScene = () => (
  <>
    <div className="relative mt-12 h-[250px] w-[min(88vw,860px)] sm:h-[280px]">
      <div className="absolute left-0 right-0 top-10 mx-auto h-14 w-full rounded-full border-4 border-black bg-[#F2B25C] sm:h-16" />
      <div className="vada-texture absolute left-0 right-0 top-[92px] mx-auto h-20 w-full rounded-full border-4 border-black sm:top-[96px] sm:h-24" />
      <div className="absolute left-0 right-0 top-[176px] mx-auto h-3 w-full rounded-full bg-chutney sm:top-[188px]" />
      <div className="absolute left-0 right-0 top-[190px] mx-auto h-14 w-full rounded-full border-4 border-black bg-[#F2B25C] sm:top-[202px] sm:h-16" />
      <div className="absolute left-[52%] top-2 w-28 -rotate-12 sm:w-36">
        <Chilli className="w-full" />
      </div>
    </div>
    <p className="mt-4 font-bungee text-sm tracking-[0.3em] text-taxi sm:text-base">2026 • THE LONG VADA™</p>
  </>
);

export const Elongification = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  return (
    <section
      id="elongification"
      ref={ref}
      data-testid="elongification-section"
      className="relative scroll-mt-16 bg-kadak"
      style={{ height: reduced ? "auto" : "190vh" }}
    >
      <div className="sticky top-0 flex min-h-screen flex-col items-center justify-center overflow-hidden py-20">
        <p className="font-bungee text-xs tracking-[0.35em] text-gulabi sm:text-sm">THE MANIFESTO</p>
        <h2 className="mt-4 max-w-4xl px-4 text-center font-shrikhand text-3xl leading-tight text-pav sm:text-5xl lg:text-6xl">
          70 saal, everyone <span className="text-mirchi">"fixed"</span> the vada pav.
        </h2>
        <p className="mt-3 px-4 text-center font-anton text-lg uppercase text-pav/80 sm:text-2xl">
          It never needed reinventing. It needed <span className="text-taxi">elongification™</span>
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3 px-4">
          {["+ CHEESE!", "+ SCHEZWAN!", "PIZZA VADA", '"GOURMET"'].map((b) => (
            <CrossedBadge key={b} label={b} />
          ))}
        </div>
        {reduced ? <StaticScene /> : <Scene p={scrollYProgress} />}
        <p className="mt-8 max-w-md px-4 text-center text-sm text-pav/60">
          Same batata heart, same chutneys, same Dadar-tapri taste. We change the shape, never the soul.
        </p>
      </div>
    </section>
  );
};
